import * as React from 'react'
import { styled, css } from '@xstyled/styled-components'
import { Heading } from '../../Text'
import { Button } from '../../Button'
import { Form } from '../Form'
import { StateField } from '../CustomFields'
import { Field } from '../Fields/Field'
import { submitToHubspot } from '../../../services'
import Script from 'next/script'
import Link from 'next/link'
import * as Yup from 'yup'

import {
  MainWrapper,
  SuccessWrapper,
  CheckboxWrapper,
  ConsentWrapper,
  FieldsWrapper as BaseFieldsWrapper,
} from './styled'

const FieldsWrapper = styled(BaseFieldsWrapper)`
  ${({ theme }) => css`
    .field--email,
    .field--best_selling_price_points,
    .field--ideal_launch_date {
      grid-column: 1 / 3;
    }
    ${theme.mediaQueries.mobile} {
      grid-template-columns: 1fr;
      .field--name {
        grid-column: auto;
      }

      button[type='submit'],
      .field--message {
        grid-column: auto;
      }
    }
  `}
`

const { useState } = React

interface WholesaleContactFormProps {
  onContinue?: () => void
}

type FormValues = {
  email: string
  firstName: string
  lastName: string
  company: string
  '0-2/domain': string
  store_name: string
  store_address: string
  number_of_locations: number
  store_overview__vibe__concept__positioning_: string
  target_customer___demographic: string
  best_selling_price_points: string
  currently_carried_designers: string
  local_competitors: string
  upcoming_markets__city_or_region_: string
  ideal_launch_date: string
  communicationsConsent: boolean
}

const formId = '0681e8aa-8e01-49b1-a129-14d37a12eabc'

export const WholesaleContactForm = ({
  onContinue,
}: WholesaleContactFormProps) => {
  const [submitting, setSubmitting] = useState(false)
  const [success, setSuccess] = useState(false)

  const handleSubmit = async (values: FormValues) => {
    setSubmitting(true)
    await fetch('/api/wholesaleContact', {
      method: 'POST',
      body: JSON.stringify(values),
    }).then((r) => r.json())
    // Convert number_of_locations to a number
    values.number_of_locations = Number(values.number_of_locations)
    await submitToHubspot(values, formId)
    setSuccess(true)
  }

  const initialValues = {
    email: '',
    firstName: '',
    lastName: '',
    company: '',
    '0-2/domain': '',
    store_name: '',
    store_address: '',
    number_of_locations: 1,
    store_overview__vibe__concept__positioning_: '',
    target_customer___demographic: '',
    best_selling_price_points: '',
    currently_carried_designers: '',
    local_competitors: '',
    upcoming_markets__city_or_region_: '',
    ideal_launch_date: '',
    communicationsConsent: true,
  }

  const validationSchema = Yup.object().shape({
    email: Yup.string()
      .email('Please enter a valid email address')
      .required('Email is required'),
    firstName: Yup.string().required('First name is required'),
    lastName: Yup.string().required('Last name is required'),
    company: Yup.string().required('Company is required'),
    '0-2/domain': Yup.string().required('Company domain is required'),
    store_name: Yup.string().required('Store name is required'),
    store_address: Yup.string().required('Store address is required'),
    number_of_locations: Yup.number()
      .typeError('Number of Locations must be a number')
      .integer('Number of Locations must be an integer')
      .min(1, 'Number of Locations must be at least 1')
      .required('Number of Locations is required'),
    store_overview__vibe__concept__positioning_: Yup.string().required(
      'Store overview is required',
    ),
    target_customer___demographic: Yup.string().required(
      'Target customer demographic is required',
    ),
    best_selling_price_points: Yup.string().required(
      'Best selling price points is required',
    ),
    currently_carried_designers: Yup.string().required(
      'Currently carried designers is required',
    ),
    local_competitors: Yup.string().required('Local competitors is required'),
    upcoming_markets__city_or_region_: Yup.string().required(
      'Upcoming markets is required',
    ),
    ideal_launch_date: Yup.string().required('Ideal launch date is required'),
    communicationsConsent: Yup.boolean().oneOf(
      [true],
      'You must consent to communications to submit this form.',
    ),
  })

  // <script src="https://js.hsforms.net/forms/embed/7668999.js" defer></script>
  // <div class="hs-form-frame" data-region="na1" data-form-id="0681e8aa-8e01-49b1-a129-14d37a12eabc" data-portal-id="7668999"></div>

  return (
    <>
      <MainWrapper>
        <Heading mt={0} mb={2} level={3}>
          Wholesale Contact Intake
        </Heading>

        <SuccessWrapper $visible={success}>
          <Heading color="body.8" level={4}>
            Thank you! We have received your request.
          </Heading>
          {onContinue ? (
            <Button onClick={onContinue} type="button" mt={3} level={3}>
              Continue shopping
            </Button>
          ) : null}
        </SuccessWrapper>
        <Form
          id="wholesale-contact-form"
          disabled={submitting}
          onSubmit={handleSubmit}
          initialValues={initialValues}
          validationSchema={validationSchema}
        >
          <FieldsWrapper $visible={!success}>
            <Field name="email" type="email" label="Email" required />
            <Field name="firstName" type="text" label="First Name" required />
            <Field name="lastName" type="text" label="Last Name" required />
            <Field name="company" type="text" label="Company" required />
            <Field
              name="0-2/domain"
              type="text"
              placeholder="spinellikilcollin.com"
              label="Company Domain"
              required
            />
            <Field name="store_name" type="text" label="Store Name" required />
            <Field
              name="store_address"
              type="text"
              label="Store Address"
              required
            />
            <Field
              name="number_of_locations"
              type="text"
              label="Number of Locations"
              required
            />
            <Field
              name="store_overview__vibe__concept__positioning_"
              type="textarea"
              placeholder="Vibe, Concept, Positioning, etc."
              label="Store Overview"
              required
            />
            <Field
              name="target_customer___demographic"
              type="textarea"
              label="Target Customer Demographic"
              required
            />
            <Field
              name="currently_carried_designers"
              type="textarea"
              label="Currently Carried Designers"
              required
            />
            <Field
              name="best_selling_price_points"
              type="text"
              label="Best Selling Price Points"
              required
            />
            <Field
              name="local_competitors"
              type="textarea"
              label="Local Competitors"
              required
            />
            <Field
              name="upcoming_markets__city_or_region_"
              type="textarea"
              label="Upcoming Markets (City or Region)"
              required
            />
            <Field
              name="ideal_launch_date"
              type="date"
              placeholder="MM/DD/YYYY"
              label="Ideal Launch Date"
              required
            />
            <ConsentWrapper>
              Spinelli Kilcollin is committed to respecting your privacy and we
              will never sell your personal information. We only use your
              information to administer your account and to provide you with the
              best experience, products and services you requested from us. From
              time to time, we may contact you about our products and services,
              as well as other content that may interest you. If you consent to
              us contacting you for this purpose, please check the box below.
            </ConsentWrapper>
            <CheckboxWrapper>
              <Field
                name="communicationsConsent"
                type="checkbox"
                label="I agree to receive other communications from Spinelli Kilcollin."
              />
            </CheckboxWrapper>
            <Button type="submit">Submit</Button>
            <ConsentWrapper>
              You may unsubscribe from these communications at any time. For
              more information on how to unsubscribe, our privacy practices, and
              how we are committed to protecting and respecting your privacy,
              please review our{' '}
              <Link href="/about/privacy-policy" target="_blank">
                Privacy Policy
              </Link>
              .
            </ConsentWrapper>
            <ConsentWrapper>
              By clicking submit, you consent to allow Spinelli Kilcollin to
              store and process the personal information submitted above to
              provide you the content requested.
            </ConsentWrapper>
          </FieldsWrapper>
        </Form>
      </MainWrapper>
    </>
  )
}
